/**
 * Helpers for showing/editing ingestion entry field values that may be
 * nested objects or arrays (otherwise String(obj) => "[object Object]").
 */

export function valueToEditableText(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

export function looksLikeJsonStructure(text) {
  if (typeof text !== 'string') return false;
  const trimmed = text.trim();
  return (
    (trimmed.startsWith('{') && trimmed.endsWith('}'))
    || (trimmed.startsWith('[') && trimmed.endsWith(']'))
  );
}

/**
 * Convert a raw payload into string values suitable for textarea editing.
 */
export function serializeRawForEditing(rawObj = {}) {
  const out = {};
  for (const [key, value] of Object.entries(rawObj || {})) {
    out[key] = valueToEditableText(value);
  }
  return out;
}

/**
 * Restore typed values from edited textarea strings using original types when available.
 * Object/array fields (or strings that look like JSON structures) are JSON.parsed.
 * Throws Error with field name when JSON is invalid for a structured field.
 */
export function deserializeRawFromEditing(editedRaw = {}, originalRaw = {}) {
  const out = {};
  for (const [key, value] of Object.entries(editedRaw || {})) {
    if (typeof value !== 'string') {
      out[key] = value;
      continue;
    }

    const original = originalRaw?.[key];
    const originalWasStructured = original !== null && typeof original === 'object';
    const shouldParseJson = originalWasStructured || looksLikeJsonStructure(value);

    if (shouldParseJson) {
      const trimmed = value.trim();
      if (trimmed === '' && originalWasStructured) {
        out[key] = Array.isArray(original) ? [] : {};
        continue;
      }
      try {
        out[key] = JSON.parse(value);
        continue;
      } catch (err) {
        const detail = err?.message || String(err);
        throw new Error(`Invalid JSON in field "${key}": ${detail}`);
      }
    }

    if (typeof original === 'number' && value.trim() !== '' && !Number.isNaN(Number(value))) {
      out[key] = Number(value);
      continue;
    }
    if (typeof original === 'boolean' && (value === 'true' || value === 'false')) {
      out[key] = value === 'true';
      continue;
    }
    if (original === null && value.trim() === '') {
      out[key] = null;
      continue;
    }

    out[key] = value;
  }
  return out;
}

/**
 * Format a value for list/read-only display (not truncated).
 */
export function formatEntryFieldDisplay(value) {
  if (value === null || value === undefined || value === '') {
    return null; // callers render empty placeholder
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  return String(value);
}
